import type { Building, GenerateBuildingFeatureSet } from "building-types/building/buildingTypes";
import { preloadBuiltinPropLibrary } from "building-types/props/getBuiltinPropLibrary";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { ensureDefined } from "common/utils/assertOfType";
import { yieldToMain } from "common/utils/asyncBatchingTools";
import { PerfMetricsRecorder } from "common/utils/perfMetricsRecorder";
import perfNow from "common/utils/perfNow";
import { BuildingGenerationContext } from "doc/utils/buildingGenerationContext";
import { AutoGrowArenaAlloc } from "geom/alloc";
import { AllocatorsBSP, getBSPAllocators, setBSPAllocators } from "geom/bspMem";
import { geomGetAllocator, geomSetAllocator } from "geom/mem";
import { buildingNew } from "../building/buildingNew";
import type { GenerateBuildingOptions, GenerateBuildingWithStagesOptions } from "../types/generateBuildingTypes";
import {
  CancelledByNewerCallError,
  isCancelledByNewerCall,
  queueGenerateBuilding,
} from "../utils/buildingGenerationQueue";

interface GenerationStageMetric {
  id: string;
  duration: number;
}

/** Amount of memory in bytes provided to the backing buffer for each stage allocator */
const STAGE_ALLOC_MAX_MEMORY = 1.5e6;

/** Stores the allocators for each generation stage */
const stageAllocs: { [stageId: string]: AutoGrowArenaAlloc } = {};
const stageBSPAllocs: { [stageId: string]: AllocatorsBSP } = {};

const defaultEnabledFeatures: GenerateBuildingFeatureSet = {
  horizontalAssemblies: false,
  roofFraming: false,
  modelComponents: false,
};

/**
 * Serializes generateBuilding calls on the same thread.
 *
 * BuildingGenerationContext (and geom/BSP allocators) is shared mutable
 * state that assumes only one build runs at a time. With `yieldInterval`,
 * awaits between stages can let another build interleave and corrupt state.
 * This mutex preserves the one-build-at-a-time invariant; workers have their
 * own module scope, so the lock is per-thread.
 */
let generationMutex: Promise<void> = Promise.resolve();

async function generateBuildingWithStagesMutex(
  options: GenerateBuildingOptions,
  signal: AbortSignal,
): Promise<Building> {
  let releaseMutex: (() => void) | undefined;
  const prevMutex = generationMutex;
  generationMutex = new Promise<void>((resolve) => {
    releaseMutex = resolve;
  });
  await prevMutex;

  try {
    return await generateBuildingWithStagesImpl(options, signal);
  } finally {
    ensureDefined(releaseMutex)();
  }
}

async function generateBuildingWithStagesImpl(
  options: GenerateBuildingOptions,
  signal: AbortSignal,
): Promise<Building> {
  if (signal.aborted) {
    throw new CancelledByNewerCallError();
  }

  const stages = options.stages;
  if (stages === undefined) {
    throw new Error("generateBuildingWithStages requires options.stages");
  }

  const {
    doc,
    optionChoices = {},
    globalParamValues = {},
    name = "Default building name",
    docHash = "",
    showPerformance = false,
    printWarningsToConsole = true,
    useArenaAlloc = false,
    useBSPObjectPools = false,
    throwOnError = false,
    benchmarkAggregator,
    onStageTiming,
    enabledFeatures = defaultEnabledFeatures,
    isolatedEntity,
    mirrorTranslationXOverride,
    mirrorInvert,
    yieldInterval = 0,
  } = options;

  const stageMetrics: GenerationStageMetric[] = [];

  if (doc && !doc.isParsed) {
    throw new Error("Studio Document has not been parsed; did you run it through docMarshal?");
  }

  const building = buildingNew(
    name,
    optionChoices,
    globalParamValues,
    doc,
    enabledFeatures,
    isolatedEntity,
    mirrorTranslationXOverride,
    mirrorInvert,
  );

  const totalT0 = Date.now();

  const oldAllocator = geomGetAllocator();
  const oldBSPAllocators = getBSPAllocators();

  try {
    if (showPerformance) {
      PerfMetricsRecorder.instance().resetAll();
    }

    let allIssues: readonly BuildingGenerationIssue[] = [];
    let lastYieldTime = yieldInterval > 0 ? perfNow() : 0;

    for (const stageId in stages) {
      if (signal.aborted) {
        throw new CancelledByNewerCallError();
      }
      const stage = stages[stageId];

      let t0: number | undefined;
      let t1: number | undefined;

      if (showPerformance || benchmarkAggregator || onStageTiming) {
        t0 = perfNow();
      }

      if (!stage.needsUpdate(building)) {
        const lastBuildingUpdate = stage.getLastValue();
        if (lastBuildingUpdate.issues) {
          allIssues = [...allIssues, ...lastBuildingUpdate.issues];
        }
        Object.assign(building, lastBuildingUpdate);
      } else {
        try {
          BuildingGenerationContext.startStage(stageId);
          if (useArenaAlloc) {
            if (!stageAllocs[stageId]) {
              stageAllocs[stageId] = new AutoGrowArenaAlloc(stageId, STAGE_ALLOC_MAX_MEMORY);
            }

            const alloc = stageAllocs[stageId];
            alloc.reset();
            geomSetAllocator(alloc);
          }

          if (useBSPObjectPools) {
            if (!stageBSPAllocs[stageId]) {
              stageBSPAllocs[stageId] = new AllocatorsBSP();
            }

            const bspAllocs = stageBSPAllocs[stageId];
            bspAllocs.reset();
            setBSPAllocators(bspAllocs);
          }

          if (stage.type === "ASYNC") {
            const buildingUpdate = await stage.generate();
            if (buildingUpdate.issues) {
              allIssues = [...allIssues, ...buildingUpdate.issues];
            }
            Object.assign(building, buildingUpdate);
          } else {
            const buildingUpdate = stage.generate();
            if (buildingUpdate.issues) {
              allIssues = [...allIssues, ...buildingUpdate.issues];
            }
            Object.assign(building, buildingUpdate);
          }
        } catch (error: unknown) {
          if (isCancelledByNewerCall(error)) {
            throw error;
          }
          console.error(error);
          if (throwOnError) {
            Object.assign(building, { issues: allIssues });
            attachPartialBuilding(error, building);
            throw error;
          }
          break;
        } finally {
          BuildingGenerationContext.endStage();
        }
      }

      if (showPerformance || benchmarkAggregator || onStageTiming) {
        t1 = perfNow();

        const dt = t1 - ensureDefined(t0);

        stageMetrics.push({ id: stageId, duration: dt });
        onStageTiming?.(stageId, dt);
      }

      if (yieldInterval > 0) {
        const now = perfNow();
        if (now - lastYieldTime >= yieldInterval) {
          await yieldToMain();
          lastYieldTime = perfNow();
          if (signal.aborted) {
            throw new CancelledByNewerCallError();
          }
        }
      }
    }

    Object.assign(building, {
      issues: allIssues,
      docHash,
      completedStages: Object.keys(stages),
    });

    const totalT1 = Date.now();

    if (showPerformance) {
      const SLOWEST_COUNT = 3;
      const slowest = stageMetrics.sort((a, b) => (a.duration < b.duration ? 1 : -1)).slice(0, SLOWEST_COUNT);
      console.group(`generateBuilding: ${(totalT1 - totalT0).toFixed(1)}ms`);
      slowest.forEach((stg) => console.log(`${stg.id} -- ${stg.duration.toFixed(1)}ms`));
      console.groupEnd();

      PerfMetricsRecorder.instance().report();
    }

    if (benchmarkAggregator) {
      benchmarkAggregator.addSample("generateBuilding", totalT1 - totalT0);
      for (const stageMetric of stageMetrics) {
        benchmarkAggregator.addSample(stageMetric.id, stageMetric.duration, undefined, ["generateBuilding"]);
      }
    }

    if (printWarningsToConsole && building.issues && building.issues.length > 0) {
      console.warn("Building generated with issues: ");
      console.warn(building.issues);
    }

    return building;
  } finally {
    geomSetAllocator(oldAllocator);
    setBSPAllocators(oldBSPAllocators);
  }
}

/**
 * Wrapper function that provides queuing behavior for building generation with an explicit
 * stage set. Multiple calls are queued - only the final call returns a building.
 * Previous calls wait for the current operation to complete and return the same result.
 *
 * If options.queueId is undefined, skips queuing and runs immediately.
 */
export default async function generateBuildingWithStages(
  options: GenerateBuildingWithStagesOptions,
): Promise<Building> {
  preloadBuiltinPropLibrary();
  return queueGenerateBuilding(options, generateBuildingWithStagesMutex);
}

function attachPartialBuilding(error: unknown, building: Building): void {
  if (typeof error === "object" && error !== null) {
    (error as { partialBuilding?: Building }).partialBuilding = building;
  }
}
